import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Datatable from "../../../core/common/dataTable";
import Api from "../../../core/data/redux/utils/apiCall";
import { toast } from "react-toastify";
import { API_URLS } from "../../../core/data/redux/apis/urls";

const parseDotNetDate = (dateStr?: string) => {
  if (!dateStr) return "-";
  const match = dateStr.match(/\/Date\((\d+)[+-]?\d*\)\//);
  if (match) {
    const ts = parseInt(match[1], 10);
    return new Date(ts).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
  }
  if (dateStr.includes('T')) {
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateStr;
};

const Channel = () => {
  const routes = all_routes;
  const location = useLocation();
  const navigate = useNavigate();
  const item = location.state?.item;

  const [channelData, setChannelData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchChannelData = useCallback(async () => {
    if (!item) return;
    setIsLoading(true);
    try {
      const payload = {
        employeeId: item?.EmployeeID ?? item?.EmpID ?? item?.employeeId ?? '',
        appraisalId: item?.AppraisalID ?? item?.appraisalId ?? item?.id ?? '',
        partAppraisalId: item?.PartAppraisalID ?? '0000',
      };

      const response: any = await Api.post(API_URLS.APAR.APPRAISAL_EMPLOYEE_CHANNEL, payload);

      console.log("APAR Channel response raw:", response);
      let dataObj = response?.data?.data || response?.data || response;
      if (Array.isArray(dataObj)) {
        dataObj = dataObj[0];
      }

      setChannelData(dataObj);
    } catch (err: any) {
      toast.error(err?.message || "Error fetching employee channel details.");
    } finally {
      setIsLoading(false);
    }
  }, [item]);

  useEffect(() => {
    fetchChannelData();
  }, [fetchChannelData]);

  if (!item) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-warning">
            No item selected. <button className="btn btn-link p-0 m-0 align-baseline" onClick={() => navigate(routes.apar.teamStatus)}>Go back</button>
          </div>
        </div>
      </div>
    );
  }

  const mergedData = channelData ? { ...item, ...channelData } : item;

  const empName = mergedData.EmployeeName || mergedData.AppraiseeName || mergedData.employeeName || 'N/A';
  const empId = mergedData.EmpID || mergedData.EmployeeID || mergedData.employeeId || 'N/A';

  // Build table rows from channel data
  let tableRows: any[] = [];
  if (Array.isArray(mergedData.participants) && mergedData.participants.length > 0) {
    tableRows = mergedData.participants.map((p: any, idx: number) => ({
      id: idx + 1,
      role: p.role || p.Role || `Officer ${idx + 1}`,
      name: p.name || p.Name || p.OfficerName || '-',
      date: parseDotNetDate(p.date || p.Date || p.ActionDate),
    }));
  } else if (Array.isArray(mergedData.channel) && mergedData.channel.length > 0) {
    tableRows = mergedData.channel.map((c: any, idx: number) => ({
      id: idx + 1,
      role: c.role || c.Role || `Officer ${idx + 1}`,
      name: c.name || c.OfficerName || c.EmployeeName || '-',
      date: parseDotNetDate(c.date || c.ActionDate || c.Date),
    }));
  } else {
    tableRows = [
      {
        id: 1,
        role: 'Controlling Officer',
        name: mergedData.ControllingOfficerName || mergedData.controllingName || mergedData.ReportingAuthority || mergedData.approver || '-',
        date: parseDotNetDate(mergedData.ControllingOfficerDate || mergedData.controllingDate || mergedData.ReportingDate),
      },
      {
        id: 2,
        role: 'Reviewing Officer',
        name: mergedData.ReviewingOfficerName || mergedData.reviewingName || mergedData.ReviewingAuthority || '-',
        date: parseDotNetDate(mergedData.ReviewingOfficerDate || mergedData.reviewingDate || mergedData.ReviewingDate),
      },
      {
        id: 3,
        role: 'Accepting Officer',
        name: mergedData.AcceptingOfficerName || mergedData.acceptingName || mergedData.AcceptingAuthority || '-',
        date: parseDotNetDate(mergedData.AcceptingOfficerDate || mergedData.acceptingDate || mergedData.AcceptingDate),
      },
    ];
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">APAR Flow Details</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to={routes.apar.dashboard}>Employee Appraisal</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.apar.teamStatus}>Team Status</Link>
                  </li>
                  <li className="breadcrumb-item active">Channel</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="card shadow-sm border-0 module-card mb-4">
            <div className="card-header bg-white border-bottom p-4 d-flex justify-content-between align-items-center">
              <h5 className="mb-0 text-primary d-flex align-items-center">
                <div className="bg-primary bg-opacity-10 text-primary rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '60px', height: '60px' }}>
                  <i className="ti ti-user fs-2"></i>
                </div>
                <div>
                  <div className="fw-bold fs-16 text-dark">{empName}</div>
                  <div className="fs-13 text-muted fw-normal mt-1">Emp ID: {empId}</div>
                </div>
              </h5>
              <div className="d-flex align-items-center gap-2">
                <button className="btn btn-outline-primary btn-sm" onClick={fetchChannelData} disabled={isLoading}>
                  {isLoading ? <><i className="ti ti-loader fa-spin me-1"></i> Loading...</> : <><i className="ti ti-refresh me-1"></i> Refresh</>}
                </button>
                <button className="btn btn-outline-secondary btn-sm" onClick={() => navigate(-1)}>
                  <i className="ti ti-arrow-left me-1"></i> Back
                </button>
              </div>
            </div>
            
            <div className="card-body p-0 overflow-hidden">
              <Datatable 
                columns={[
                  {
                    title: "S.No",
                    dataIndex: "id",
                    key: "id",
                    width: 80,
                  },
                  {
                    title: "Role",
                    dataIndex: "role",
                    key: "role",
                    render: (text: string) => <span className="fw-medium text-dark">{text}</span>
                  },
                  {
                    title: "Officer Name",
                    dataIndex: "name",
                    key: "name",
                    render: (text: string) => (
                      <span className="text-secondary d-flex align-items-center"><i className="ti ti-user-check me-2 fs-4 text-muted"></i>{text}</span>
                    )
                  },
                  {
                    title: "Action Date",
                    dataIndex: "date",
                    key: "date",
                    render: (text: string) => (
                      <span className="text-secondary d-flex align-items-center"><i className="ti ti-calendar me-2 fs-4 text-muted"></i>{text}</span>
                    )
                  }
                ]} 
                dataSource={tableRows} 
                loading={isLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Channel;
